import { useMemo } from 'react';
import * as echarts from 'echarts';
import { VariableType } from '../../../types/dashboard.types';
import { ChartDataPoint, VariableConfig, ChartMode, TooltipOrderMode } from '../types';
import {
  getAllSeriesKeys,
  createModelRunSeries,
  createStatisticalSeries,
} from '../utils/chartSeries';
import { formatValue } from '../utils/valueFormatting';
import { createTooltipConfig } from '../utils/tooltipConfig';
import { createLegendConfig } from '../utils/legendConfig';
import { createDataZoomConfig, DataZoomRange } from '../utils/dataZoomConfig';
import { createXAxisConfig } from '../utils/axisConfig';

interface UseChartOptionsProps {
  chartData: ChartDataPoint[];
  selectedVariable: VariableType;
  currentConfig: VariableConfig;
  isDark: boolean;
  axisColor: string;
  chartMode: ChartMode;
  tooltipOrderMode: TooltipOrderMode;
  timezone?: string;
  formatInUserTimezone?: (timestamp: string, format: string) => string;
  initialZoomRange?: DataZoomRange;
}

export const useChartOptions = ({
  chartData,
  selectedVariable,
  currentConfig,
  isDark,
  axisColor,
  chartMode,
  tooltipOrderMode,
  timezone,
  formatInUserTimezone,
  initialZoomRange,
}: UseChartOptionsProps): echarts.EChartsOption => {
  return useMemo(() => {
    if (!chartData.length) {
      return {};
    }

    // Generate series for chart
    const dataKeys = getAllSeriesKeys(chartData);
    // Use area style only in delta mode (to avoid visual clutter with statistical band in data mode)
    const modelSeries = createModelRunSeries(chartData, dataKeys, chartMode === 'delta');
    const statisticalSeries =
      chartMode === 'data' ? createStatisticalSeries(chartData, isDark) : [];
    const series = [...modelSeries, ...statisticalSeries];

    const option: echarts.EChartsOption = {
      backgroundColor: 'transparent',
      animation: true,
      animationDuration: 300,
      animationEasing: 'cubicOut',
      grid: {
        left: '6%',
        right: '4%',
        bottom: '18%',
        top: '16%',
        containLabel: true,
      },
      tooltip: createTooltipConfig(
        selectedVariable,
        currentConfig,
        isDark,
        tooltipOrderMode,
        timezone,
        formatInUserTimezone,
      ),
      legend: createLegendConfig(isDark, axisColor),
      dataZoom: createDataZoomConfig(isDark, axisColor, initialZoomRange),
      xAxis: createXAxisConfig(isDark, axisColor, timezone, formatInUserTimezone),
      yAxis: {
        type: 'value',
        name: chartMode === 'delta' ? `Δ ${currentConfig.yAxisName}` : currentConfig.yAxisName,
        nameLocation: 'middle',
        nameGap: 40,
        nameTextStyle: {
          color: axisColor,
          fontSize: 10,
          fontWeight: 500,
        },
        axisLabel: {
          color: axisColor,
          fontSize: 9,
          formatter: (value: number) => formatValue(value, selectedVariable),
        },
        splitLine: {
          lineStyle: {
            color: isDark ? '#374151' : '#E5E7EB',
            type: 'dashed',
          },
        },
        axisLine: {
          lineStyle: {
            color: axisColor,
          },
        },
      },
      series,
    };

    return option;
  }, [
    chartData,
    selectedVariable,
    currentConfig,
    isDark,
    axisColor,
    chartMode,
    tooltipOrderMode,
    timezone,
    formatInUserTimezone,
    initialZoomRange,
  ]);
};
